/* eslint-disable react/jsx-props-no-spreading */
/**
 * @fileoverview Organism component for the customer detail edit form in the inbox.
 * Allows editing standard and custom fields, with validation, optimized logic and internationalized design.
 * @module CustomerDetailFormMC
 * @category Component - Organism
 * @since 1.0.0
 * @version 2.1.0
 */
import React, { memo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Controller } from 'react-hook-form';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import ButtonComponent from "../../../components/ButtonMC";
import FormProviderMC from "../../../components/FormProviderMC";
import InputComponent from "../../../components/InputMC";
import useCustomerDetailLogic from "../../../hooks/inbox/forms/useCustomerDetailLogic";
import useCustomerDetailForm from "../../../hooks/inbox/forms/useCustomerDetailForm";
import useCustomerDetailActions from "../../../hooks/inbox/actions/useCustomerDetailActions";
import useCustomerDetailFormUI from "../../../hooks/inbox/ui/useCustomerDetailFormUI";
import DatePickerMC from "../../../components/DatePickerMC";
import ToggleSwitchMC from "../../../components/ToggleSwitchMC";
import { useTraceUpdate } from "../../../hooks/shared/useDebugRenders";
import DropdownMC from "../../../components/DropdownMC";

/**
 * Organism component for the customer detail edit form in the inbox.
 *
 * Atomic Design: Organism
 * - Composed by InputComponent, DropdownMC, DatePickerMC, ToggleSwitchMC, ButtonComponent
 * - Allows editing standard and custom fields, with validation and logic separated by hooks
 * - Uses TailwindCSS with .tw-wrapper
 * - Internationalized translation with useTranslation
 * - Optimized with memo and useCallback
 */
const CustomerDetailFormMC = memo(({
  customer,
  onClose,
  customerFields,
  customFields,
  platform,
  refreshCustomerData
}) => {
  const { t } = useTranslation('InboxMC');

  // Logic layer: data processing and formatting
  const { customFieldsGrouped } = useCustomerDetailLogic(customerFields, customFields);

  // Form layer: state and validation
  const {
    formMethods,
    transformFormData,
    isFormValid,
    shouldShowPhoneFields
  } = useCustomerDetailForm(
    customer,
    customFieldsGrouped,
    platform
  );

  // WhatsApp without a real phone (user_id-only / empty / "+") must show country + phone
  const showPhoneFields = shouldShowPhoneFields || customer?.hasPhone === false;

  // Actions layer: business operations
  const { loading, saveCustomer } = useCustomerDetailActions();

  // UI layer: presentation configurations
  const {
    countryOptions,
    documentTypeOptions,
    saveButtonConfig,
    inputClassName,
    countryFilterOption,
    renderCountryOption,
    renderSelectedCountry
  } = useCustomerDetailFormUI(loading);

  const handleSaveButtonClick = useCallback(() => {
    formMethods.handleSubmit(async (formData) => {
      const transformedData = transformFormData(formData);

      await saveCustomer(transformedData, {
        onSuccess: () => {
          onClose?.();
          refreshCustomerData?.();
        }
      });
    })();
  }, [formMethods, transformFormData, saveCustomer, onClose, refreshCustomerData]);

  // 🚀 DEBUG RENDER - Track re-renders in CustomerDetailFormMC (OPTIMIZED)
  useTraceUpdate('CustomerDetailFormMC', {
    customerId: customer?.id, // Only the ID
    customerFieldsLength: customerFields?.length || 0,
    customFieldsLength: customFields?.length || 0,
    onCloseRef: !!onClose, // Only if the function exists
    customFieldsGroupedLength: customFieldsGrouped?.length || 0,
    loading,
    isFormValid
  }, {
    logRenderCount: true,
    logPropChanges: true
  });

  return (
    <div className="tw-wrapper">
      <div className="tw-flex tw-flex-col tw-h-[calc(100vh-250px)] tw-max-h-screen tw-min-h-[400px]">
        <div className="tw-flex tw-justify-between tw-items-center tw-flex-shrink-0 tw-mb-3">
          <span className="tw-text-sm tw-font-medium tw-text-gray-950">
            {t('customerDetails.title')}
          </span>
          <ButtonComponent
            {...saveButtonConfig}
            onClick={handleSaveButtonClick}
            data-tooltip-id="details-button-tooltip"
          />
          <ReactTooltip
            id="details-button-tooltip"
            className="tw-z-50"
          >
            <span>{t('customerDetails.saveButtonTooltip')}</span>
          </ReactTooltip>
        </div>
        <div className="tw-flex-1 tw-overflow-y-auto tw-min-h-0 tw-pr-1">
          <FormProviderMC methods={formMethods}>
            <InputComponent
              useFloatingLabel
              label={t('customerDetails.firstName')}
              name="first_name"
              className={inputClassName}
            />
            <InputComponent
              useFloatingLabel
              label={t('customerDetails.lastName')}
              name="last_name"
              className={inputClassName}
            />
            {showPhoneFields && (
              <>
                <DropdownMC
                  useFloatingLabel
                  options={countryOptions}
                  label={t('customerDetails.country')}
                  name="country_id"
                  defaultValue={customer?.country_id || ''}
                  className="tw-mb-3"
                  search
                  filterOption={countryFilterOption}
                  renderOption={renderCountryOption}
                  renderSelected={renderSelectedCountry}
                />
                <InputComponent
                  useFloatingLabel
                  label={t('customerDetails.phone')}
                  name="phone"
                  className={inputClassName}
                />
              </>
            )}
            <InputComponent
              useFloatingLabel
              label={t('customerDetails.email')}
              name="email"
              className={inputClassName}
            />
            <DropdownMC
              useFloatingLabel
              options={documentTypeOptions}
              label={t('customerDetails.documentType')}
              name="id_type"
              defaultValue={customer?.id_type || ''}
              className="tw-mb-3"
            />
            <InputComponent
              useFloatingLabel
              label={t('customerDetails.identification')}
              name="id_number"
              className={inputClassName}
            />
            <InputComponent
              textarea
              useFloatingLabel
              rows={2}
              label={t('customerDetails.notes')}
              name="notes"
              className={inputClassName}
            />
            {customFieldsGrouped?.map((field, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={`customer-custom-field-view-${field.identifier}-${index}`}>
                {field.field_type === "string" && (
                  <InputComponent
                    useFloatingLabel
                    label={field.name}
                    name={`related_data.${field.identifier}`}
                    className={inputClassName}
                  />
                )}
                {field.field_type === "date" && (
                  <DatePickerMC
                    label={field.name}
                    name={`related_data.${field.identifier}`}
                    className={inputClassName}
                    dateFormat="yyyy-MM-dd"
                  />
                )}
                {(field.field_type === "integer" || field.field_type === "float") && (
                  <InputComponent
                    type="number"
                    useFloatingLabel
                    label={field.name}
                    name={`related_data.${field.identifier}`}
                    className={inputClassName}
                  />
                )}
                {field.field_type === "boolean" && (
                  <Controller
                    name={`related_data.${field.identifier}`}
                    control={formMethods.control}
                    render={({ field: controllerField }) => (
                      <ToggleSwitchMC
                        label={field.name}
                        isOn={!!controllerField.value}
                        handleToggle={() => {
                          controllerField.onChange(!controllerField.value);
                        }}
                        containerClassName="tw-mb-3"
                        labelClassName="tw-text-blue-500"
                      />
                    )}
                  />
                )}
                {field.field_type === "list" && (
                  <DropdownMC
                    useFloatingLabel
                    options={field.list_options?.map((option) => ({
                      id: option.value,
                      ...option
                    })) || []}
                    label={field.name}
                    name={`related_data.${field.identifier}`}
                    defaultValue={field.answer || ''}
                    className="tw-mb-3"
                  />
                )}
              </div>
            ))}
            <div className="tw-flex tw-mb-3">
              <ButtonComponent
                text={t('customerDetails.save')}
                onClick={handleSaveButtonClick}
                className="tw-flex-1"
                disabled={loading}
              />
            </div>
          </FormProviderMC>
        </div>
      </div>
    </div>
  );
});

CustomerDetailFormMC.displayName = "CustomerDetailFormMC";

export default CustomerDetailFormMC;
